import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Mail, Phone, Calendar, Camera, CheckCircle2 } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useApp } from '../context/AppContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
export function EditProfileScreen() {
  const {
    goBack
  } = useNavigation();
  const {
    user,
    setUser
  } = useApp();
  // Form State
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  // UI State
  const [avatarToggle, setAvatarToggle] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Check if form has changes
  const hasChanges = name !== (user?.name || '') || email !== (user?.email || '') || phone !== (user?.phone || '') || dob !== '' || gender !== '' || avatarToggle;
  const handleSave = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      if (user) {
        setUser({
          ...user,
          name,
          email,
          phone
          // In a real app, we'd save dob and gender to the user object too
        });
      }
      setLoading(false);
      setShowSuccess(true);
      setTimeout(() => {
        goBack();
      }, 1500);
    }, 1000);
  };
  const handleAvatarClick = () => {
    setAvatarToggle(!avatarToggle);
  };
  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }
    if (value.length <= 10) {
      setDob(value);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-478">
      <Header title="Edit Profile" data-id="element-479" />

      <div className="p-4 space-y-4" data-id="element-480">
        {/* Avatar Section */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-50 flex flex-col items-center" data-id="element-481">
          <div className="relative mb-3" data-id="element-482">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center overflow-hidden transition-colors ${avatarToggle ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'}`} data-id="element-483">
              {user?.avatar && !avatarToggle ? <img src={user.avatar} alt={name} className="w-full h-full object-cover" data-id="element-484" /> : <UserIcon className="w-10 h-10" data-id="element-485" />}
            </div>
            <button onClick={handleAvatarClick} className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center shadow-md border-2 border-white hover:bg-primary-600 active:scale-95 transition-all" data-id="element-486">
              <Camera className="w-4 h-4" data-id="element-487" />
            </button>
          </div>
          <h2 className="text-lg font-bold text-gray-900" data-id="element-488">
            {name || 'Guest User'}
          </h2>
          <p className="text-sm text-gray-500" data-id="element-489">
            Tap the camera icon to change photo
          </p>
        </div>

        {/* Form Section */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-50 space-y-4" data-id="element-490">
          <Input label="Full Name" placeholder="John Doe" value={name} onChange={e => setName(e.target.value)} icon={<UserIcon className="w-4 h-4" data-id="element-492" />} data-id="element-491" />

          <Input label="Email Address" type="email" placeholder="john@example.com" value={email} onChange={e => setEmail(e.target.value)} icon={<Mail className="w-4 h-4" data-id="element-494" />} data-id="element-493" />

          <Input label="Phone Number" type="tel" placeholder="+234 800 000 0000" value={phone} onChange={e => setPhone(e.target.value)} icon={<Phone className="w-4 h-4" data-id="element-496" />} data-id="element-495" />

          <Input label="Date of Birth" placeholder="DD/MM/YYYY" value={dob} onChange={handleDobChange} icon={<Calendar className="w-4 h-4" data-id="element-498" />} inputMode="numeric" data-id="element-497" />

          <div data-id="element-499">
            <label className="text-sm font-medium text-gray-700 block mb-2" data-id="element-500">
              Gender
            </label>
            <div className="flex gap-3" data-id="element-501">
              {['Male', 'Female', 'Other'].map(g => <button key={g} onClick={() => setGender(g)} className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${gender === g ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-502">
                  {g}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-safe z-40" data-id="element-503">
        <div className="max-w-md mx-auto" data-id="element-504">
          <Button fullWidth size="lg" onClick={handleSave} loading={loading} disabled={!hasChanges || !name || !email} className="shadow-lg shadow-primary/20" data-id="element-505">
            Save Changes
          </Button>
        </div>
      </div>

      {/* Success Toast */}
      <AnimatePresence data-id="element-506">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-24 left-4 right-4 z-50 flex justify-center" data-id="element-507">
            <div className="bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-2" data-id="element-508">
              <CheckCircle2 className="w-5 h-5 text-success" data-id="element-509" />
              <span className="text-sm font-medium" data-id="element-510">
                Profile updated successfully
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}